import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useRef } from 'react';

import { documentHubApi } from '../documentHub.api';
import { CompanyDocument, DocumentStatus } from '../documentHub.schema';

export const DOCUMENTS_QUERY_KEY = 'document-hub-documents';

const LIST_POLL_INTERVAL_MS = 2000;

// Safety net: stop the in-flight poll after this long even if a document is
// still `uploaded`/`detecting`. Detection (FD-3689) normally settles in seconds;
// this generous ceiling stops a single wedged document (detection never
// completes) from polling GET /documents every 2s indefinitely. Larger than the
// original short cap since detection is now live — this is only a backstop.
const MAX_IN_FLIGHT_POLL_MS = 5 * 60 * 1000;

/** True while any document is still uploading/detecting (drives poll cadence). */
export function hasInFlight(docs: CompanyDocument[] | undefined): boolean {
  return (docs ?? []).some(
    (d) => d.status === DocumentStatus.Uploaded || d.status === DocumentStatus.Detecting
  );
}

/**
 * True while any requested ZIP extraction has not yet produced its children.
 * A ZIP is never persisted as a document, so a zip-only upload leaves the list
 * empty until the backend fans it out; this keeps the poll alive in that window.
 * Children are correlated by their `{zipKey}/extracted/` s3Key prefix.
 */
export function hasPendingExtraction(
  docs: CompanyDocument[] | undefined,
  extractedZipKeys: string[]
): boolean {
  return extractedZipKeys.some((key) => !(docs ?? []).some((d) => d.s3Key.startsWith(`${key}/extracted/`)));
}

/**
 * Documents list query, optionally scoped to a company (`companyId`).
 *
 * Called twice by the Docs Hub (see DocumentHub.tsx): once unscoped as the source
 * for the processing banner, review flow, and upload dedup validation, and once
 * scoped to back the table view. When no company is selected both calls share the
 * `[KEY, null]` key and dedupe to a single request.
 *
 * Polls while any document is still in-flight (uploaded/detecting) so detection
 * progress + the banner update live, then settles on its own once every row
 * reaches detected/confirmed/failed. Because the banner and review flow read
 * this server state (rather than an ephemeral client-side upload batch), they
 * survive navigation away and back.
 */
export function useDocuments(
  extractedZipKeys: string[] = [],
  companyId?: number | null
): UseQueryResult<CompanyDocument[], Error> {
  // Wall-clock start of the current in-flight streak; reset once nothing is in
  // flight so a later upload gets a fresh ceiling.
  const inFlightSinceRef = useRef<number | null>(null);
  return useQuery({
    // `companyId` is part of the key so selecting/clearing a company refetches
    // the scoped list rather than serving the previously-cached one.
    queryKey: [DOCUMENTS_QUERY_KEY, companyId ?? null],
    queryFn: () => documentHubApi.listDocuments(companyId ?? undefined),
    // Keep the prior rows on screen while a new company scope loads, instead of
    // dropping to `[]` and flashing the "No documents" empty state on each switch.
    placeholderData: keepPreviousData,
    // A row's status decides what a person may click and can change while they are away, so the
    // list is re-read on entry and on tab return instead of served from the app-wide cache.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      // Poll while anything is still uploading/detecting, or while a ZIP is still
      // fanning its children out — both resolve asynchronously server-side.
      const pending =
        hasInFlight(query.state.data) || hasPendingExtraction(query.state.data, extractedZipKeys);
      if (!pending) {
        inFlightSinceRef.current = null;
        return false;
      }
      const now = Date.now();
      inFlightSinceRef.current ??= now;
      if (now - inFlightSinceRef.current > MAX_IN_FLIGHT_POLL_MS) {
        return false; // Backstop: give up on a wedged batch (see FD-3689).
      }
      return LIST_POLL_INTERVAL_MS;
    },
  });
}
